/* src/components/Commission/CarrierContent/index.js */
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import Tree from 'rc-tree';
import Skeleton from 'react-loading-skeleton';

import Api from '../../../api';
import Button from '../../common/Button';
import { humanize } from '../../../utils/words';
import useAccessToken from '../../../utils/useAccessToken';
import useLoader from '../../../utils/useLoader';

import toast from 'react-hot-toast';

// ---------------------------------------------------------------------
// File-tree sub-component (fixed path → /sites/americo/services/commissions)
// ---------------------------------------------------------------------
function CarrierContentFilesDisplay({ organization, isGenerated }) {
  const { organizationData } = useAccessToken();
  const { isLoading, setIsLoading } = useLoader();
  const [treeData, setTreeData] = useState([]);

  const GCS_PATH = `orgs/${organizationData.id}/sites/americo/services/commissions/records/`;

  const getOrgFiles = async () => {
    setIsLoading(true);

    if (!organization.id) {
      setIsLoading(false);
      return;
    }

    const res = await Api.CommissionsService.retrieveCarrierFilesTree(GCS_PATH);

    if (res.success) setTreeData(res.data);
    setIsLoading(false);
  };

  const download = async (fileDir) => {
    setIsLoading(true);
    await Api.FirebaseStorage.Documents.download(fileDir);
    setIsLoading(false);
  };

  const renderTree = (data) =>
    data.map((node) => ({
      key: node.key,
      title: (
        <div className="tree-node-title-div">
          <div>{node.title}</div>
          <div>{node.isLeaf ? node.metadata.updated : ''}</div>
          <div>
            {node.isLeaf && (
              <span onClick={() => download(node.fullPath)} className="download-button">
                Download
              </span>
            )}
          </div>
        </div>
      ),
      children: node.children ? renderTree(node.children) : [],
      isLeaf: node.isLeaf,
    }));

  useEffect(() => {
    getOrgFiles();           // refresh on mount & when rerun flag flips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerated]);

  return (
    <div className="carrier-content-files-display">
      <h2>Commission Files</h2>
      {isLoading ? (
        <Skeleton count={5} />
      ) : treeData.length ? (
        <Tree
          className="tree"
          showLine
          selectable={false}
          treeData={renderTree(treeData)}
          autoExpandParent
        />
      ) : (
        <h3>No results found</h3>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------
// Main wrapper – keeps the upload UI you already had
// ---------------------------------------------------------------------
function CarrierContent({ selectedCarrier, organization, carriers }) {
  const { isLoading, setIsLoading } = useLoader();
  const { userData } = useAccessToken();

  const validatePDF = (values) => {
    const errors = {};
    if (!values.pdf)
      errors.pdf = 'Please upload a PDF file';
    return errors;
  };

  const formik = useFormik({
    initialValues: {},
    validate: validatePDF,
    onSubmit: async (vals) => {
      try {
        setIsLoading(true);

        const carrier = carriers.find(({id}) => id === selectedCarrier);

        const result = await Api.CommissionsService.processCommissionFile(
          vals.pdf,
          { id: carrier.id, label: carrier.label },
          organization,
          userData.id
        );

        setIsLoading(false);

        if (result.success) toast.success('Commission file uploaded and processed! Your file should start downloading now.');
        else throw Error(result.error)
      } catch (error) {
        setIsLoading(false);
        toast.error('Something went wrong while processing the file. Please try again or contact support.');
      }
  },
});

  return (
    <>
      <h1>{humanize('commissions')}</h1>

      <div className="carrier-content-file-input">
        <form onSubmit={formik.handleSubmit}>
          <input
            type="file"
            name="pdf"
            accept="application/pdf"
            disabled={isLoading}
            onChange={(e) => formik.setFieldValue('pdf', e.currentTarget.files?.[0])}
          />
          <Button type="submit" isLoading={isLoading} label="Extract Data" />
        </form>

        {formik.errors.pdf && <div className="input-error-message">{formik.errors.pdf}</div>}
      </div>
    </>
  );
}

export default CarrierContent;
